import { Hct, argbFromHex, hexFromArgb } from "@material/material-color-utilities";

import i18n, { type TranslationKey } from "@/i18n";

/**
 * The 29 palette keys map 1:1 to the uploader CSS variables in
 * wedding-upload/src/index.scss (each key is the variable name without the
 * leading `--`). Grouped by family for the detailed editor.
 */
export const PALETTE_GROUPS = [
  {
    family: "background",
    labelKey: "colorEditor.families.background" satisfies TranslationKey,
    keys: ["background", "background-muted", "on-background", "on-background-muted"],
  },
  {
    family: "primary",
    labelKey: "colorEditor.families.primary" satisfies TranslationKey,
    keys: ["primary", "primary-highlight", "primary-muted", "on-primary", "on-primary-muted"],
  },
  {
    family: "secondary",
    labelKey: "colorEditor.families.secondary" satisfies TranslationKey,
    keys: ["secondary", "secondary-highlight", "secondary-muted", "on-secondary", "on-secondary-muted"],
  },
  {
    family: "error",
    labelKey: "colorEditor.families.error" satisfies TranslationKey,
    keys: ["error", "error-highlight", "error-muted", "on-error", "on-error-muted"],
  },
  {
    family: "success",
    labelKey: "colorEditor.families.success" satisfies TranslationKey,
    keys: ["success", "success-highlight", "success-muted", "on-success", "on-success-muted"],
  },
  {
    family: "warning",
    labelKey: "colorEditor.families.warning" satisfies TranslationKey,
    keys: ["warning", "warning-highlight", "warning-muted", "on-warning", "on-warning-muted"],
  },
] as const;

export const PALETTE_KEYS = PALETTE_GROUPS.flatMap((g) => g.keys);

/**
 * Families the operator can override. Each is derived from the master `seed`
 * unless an explicit value is supplied — Material-style: the seed drives the
 * theme but is never used verbatim as a web color.
 */
export const OVERRIDE_KEYS = ["primary", "secondary", "background", "error", "success", "warning"] as const;
export type OverrideKey = (typeof OVERRIDE_KEYS)[number];

export type Seeds = { seed: string } & Partial<Record<OverrideKey, string>>;
export type Palette = Record<string, string>;

export interface ColorScheme {
  seeds: Seeds;
  palette: Palette;
}

/**
 * The master seed used when a site has no colorScheme yet. Acts purely as a
 * generation seed — it is never emitted as a CSS variable.
 */
export const DEFAULT_SEED = "#cab070";

/**
 * Default family overrides — the uploader's hand-built theme
 * (wedding-upload/src/index.scss). Paired with DEFAULT_SEED so a brand-new
 * site reproduces today's theme exactly.
 */
export const DEFAULT_OVERRIDES: Required<Record<OverrideKey, string>> = {
  primary: "#cab070",
  secondary: "#568f9f",
  background: "#112922",
  error: "#d9534f",
  success: "#3fa34d",
  warning: "#f0ad4e",
};

/** The full default scheme seeds (master seed + every family override). */
export const DEFAULT_SEEDS: Seeds = { seed: DEFAULT_SEED, ...DEFAULT_OVERRIDES };

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

export function isHexColor(value: string): boolean {
  return HEX_COLOR.test(value.trim());
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function hctOf(hex: string): Hct {
  return Hct.fromInt(argbFromHex(hex));
}

function toHex(hue: number, chroma: number, tone: number): string {
  return hexFromArgb(Hct.from(((hue % 360) + 360) % 360, Math.max(chroma, 0), clamp(tone, 0, 100)).toInt());
}

/**
 * Derives the five variants of a standard color family (primary, secondary,
 * error, success, warning) from a single seed using HCT tone targets tuned to
 * approximate the uploader's original dark theme.
 */
function familyVariants(prefix: string, seedHex: string): Palette {
  const c = hctOf(seedHex);
  return {
    [prefix]: hexFromArgb(c.toInt()),
    [`${prefix}-highlight`]: toHex(c.hue, c.chroma, clamp(c.tone + 10, 0, 92)),
    [`${prefix}-muted`]: toHex(c.hue, Math.max(c.chroma * 0.45, 6), 36),
    [`on-${prefix}`]: toHex(c.hue, Math.min(c.chroma, 8), 16),
    [`on-${prefix}-muted`]: toHex(c.hue, Math.min(c.chroma, 10), 90),
  };
}

/**
 * Derives the four background-family variants (dark theme) from a background
 * seed: a near-black tinted surface with neutral foreground tones.
 */
function backgroundVariants(seedHex: string): Palette {
  const c = hctOf(seedHex);
  return {
    background: hexFromArgb(c.toInt()),
    "background-muted": toHex(c.hue, c.chroma, clamp(c.tone + 2, 0, 100)),
    "on-background": toHex(c.hue, Math.min(c.chroma, 4), 88),
    "on-background-muted": toHex(c.hue, Math.min(c.chroma, 4), 74),
  };
}

/**
 * Generates the full 29-key palette from the master `seed`. Every family is
 * derived from the seed unless an explicit override is supplied:
 *  - primary: seed hue/chroma raised to a canonical light tone (never the seed verbatim)
 *  - secondary: seed hue rotated ~150° (complementary), moderate chroma
 *  - background: seed hue at very low chroma, near-black tone
 *  - error/success/warning: canonical status hues (preserve their meaning)
 */
export function generatePalette(seeds: Seeds): Palette {
  const s = hctOf(seeds.seed);

  const primary = seeds.primary ?? toHex(s.hue, Math.max(s.chroma, 24), 72);
  const secondary =
    seeds.secondary ?? toHex(s.hue + 150, Math.max(s.chroma, 28), 62);
  const background =
    seeds.background ?? toHex(s.hue, 12, 16);
  const error = seeds.error ?? DEFAULT_OVERRIDES.error;
  const success = seeds.success ?? DEFAULT_OVERRIDES.success;
  const warning = seeds.warning ?? DEFAULT_OVERRIDES.warning;

  return {
    ...backgroundVariants(background),
    ...familyVariants("primary", primary),
    ...familyVariants("secondary", secondary),
    ...familyVariants("error", error),
    ...familyVariants("success", success),
    ...familyVariants("warning", warning),
  };
}

/**
 * Builds a complete ColorScheme (seeds + resolved palette) from seeds.
 */
export function buildColorScheme(seeds: Seeds): ColorScheme {
  return { seeds, palette: generatePalette(seeds) };
}

/** The hues (degrees) that read as "red" and "green" for the status guardrail. */
function hueOf(hex: string): number {
  return hctOf(hex).hue;
}

/**
 * Returns a warning message if a status color has drifted away from its
 * conventional hue (error→red, success→green), which can confuse guests.
 * Non-blocking — purely advisory.
 */
export function statusColorWarning(palette: Palette): string | null {
  const errorColor = palette.error;
  const successColor = palette.success;
  if (!errorColor || !successColor) return null;
  const errorHue = hueOf(errorColor);
  const successHue = hueOf(successColor);
  // Red sits near 0°/360° (wraps); allow a generous band.
  const isRed = errorHue <= 35 || errorHue >= 330;
  // Green sits near ~120–160° in HCT.
  const isGreen = successHue >= 95 && successHue <= 175;
  if (isRed && isGreen) return null;
  if (!isRed && !isGreen) return i18n.t("colorEditor.statusWarnBoth");
  return !isRed ? i18n.t("colorEditor.statusWarnError") : i18n.t("colorEditor.statusWarnSuccess");
}
